import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@dfws/ui";
import { api } from "../../lib/api";
import s from "./Settings.module.css";

interface Health {
  domain: string;
  generatedAt: string;
  reports: number;
  posture: { key: string; label: string; ok: boolean; detail: string }[];
  dmarc: { days: number; messages: number; failed: number; spoofed: number; sources: { ip: string; messages: number; passed: number; failed: number; rejected: number; reporters: string[]; ours: boolean }[] };
  tls: { days: number; ok: number; failed: number; failures: { type: string; sendingIp: string | null; count: number; reporter: string; day: string }[] };
}

const n = (x: number) => x.toLocaleString();

/** DMARC and TLS reports about our domain, read from the admin's mailbox, and the DNS records that protect it. */
export function MailHealthSection() {
  const q = useQuery({ queryKey: ["mail-health"], queryFn: () => api<Health>("/admin/mail-health"), staleTime: 10 * 60_000 });
  const refresh = useQuery({ queryKey: ["mail-health-refresh"], queryFn: () => api<Health>("/admin/mail-health?refresh"), enabled: false });
  const d = refresh.data ?? q.data;
  if (q.isPending) return <p className={s.muted}>Reading the last 60 days of reports. This takes a few seconds.</p>;
  if (q.isError || !d) return <p className={s.err + " " + s.notice}>{q.error?.message ?? "Mail health could not be loaded."}</p>;
  return (
    <>
      <p className={s.lead}>
        What other mail providers report about mail sent as {d.domain}, from the last 60 days of DMARC and TLS reports ({n(d.reports)} reports), and the DNS records that protect the domain.
      </p>
      <section className={s.group} aria-labelledby="mh-posture">
        <h3 id="mh-posture" className={s.groupTitle}>Domain protection</h3>
        <ul className={s.list} style={{ marginTop: "var(--space-3)" }}>
          {d.posture.map((p) => (
            <li key={p.key} className={s.listItem}>
              {p.ok ? <CheckCircle2 aria-label="OK" style={{ color: "var(--color-success)", width: 18, flex: "none" }} /> : <AlertTriangle aria-label="Needs attention" style={{ color: "var(--color-warning)", width: 18, flex: "none" }} />}
              <div className={s.listMain}>
                <div className={s.listTitle}>{p.label}</div>
                <div className={s.listSub}>{p.detail}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <section className={s.group} aria-labelledby="mh-dmarc">
        <h3 id="mh-dmarc" className={s.groupTitle}>Who sends as {d.domain} (DMARC, {d.dmarc.days} days)</h3>
        <div className={s.stats}>
          <Stat label="Messages reported" value={n(d.dmarc.messages)} />
          <Stat label="Failed checks" value={n(d.dmarc.failed)} tone={d.dmarc.failed ? "warn" : "ok"} />
          <Stat label="Spoofing attempts" value={n(d.dmarc.spoofed)} tone={d.dmarc.spoofed ? "warn" : "ok"} hint="Failed mail from servers that are not ours. p=reject means receivers bin it." />
        </div>
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr><th scope="col">Sending server</th><th scope="col">Messages</th><th scope="col">Passed</th><th scope="col">Failed</th><th scope="col">Reported by</th></tr>
            </thead>
            <tbody>
              {d.dmarc.sources.map((x) => (
                <tr key={x.ip}>
                  <td><span className={s.mono}>{x.ip}</span>{x.ours && <span className={s.chip} style={{ marginLeft: 6 }}>our server</span>}</td>
                  <td className="df-tabular">{n(x.messages)}</td>
                  <td className="df-tabular">{n(x.passed)}</td>
                  <td className="df-tabular" style={x.failed ? { color: "var(--color-danger)" } : undefined}>{n(x.failed)}</td>
                  <td>{x.reporters.join(", ")}</td>
                </tr>
              ))}
              {!d.dmarc.sources.length && <tr><td colSpan={5} className={s.muted}>No DMARC reports in the last 60 days.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <section className={s.group} aria-labelledby="mh-tls">
        <h3 id="mh-tls" className={s.groupTitle}>Encrypted delivery to us (TLS reports, {d.tls.days} days)</h3>
        <div className={s.stats}>
          <Stat label="Encrypted sessions" value={n(d.tls.ok)} />
          <Stat label="Failed sessions" value={n(d.tls.failed)} tone={d.tls.failed ? "warn" : "ok"} />
        </div>
        {d.tls.failures.length > 0 && (
          <ul className={s.list}>
            {d.tls.failures.map((f, i) => (
              <li key={i} className={s.listItem}><div className={s.listMain}><div className={s.listTitle}>{f.type}</div><div className={s.listSub}>{f.day} · {f.reporter} · {f.sendingIp ?? "unknown sender"} · {f.count} sessions</div></div></li>
            ))}
          </ul>
        )}
      </section>
      <div className={s.actionsBar}>
        <Button icon={<RefreshCw />} loading={refresh.isFetching} onClick={() => void refresh.refetch()}>Read reports again</Button>
        <span className={s.muted}>Updated {new Date(d.generatedAt).toLocaleString()}</span>
      </div>
    </>
  );
}

function Stat({ label, value, tone, hint }: { label: string; value: string; tone?: "ok" | "warn"; hint?: string }) {
  return (
    <div className={s.stat} title={hint}>
      <div className={s.statValue} style={tone === "warn" ? { color: "var(--color-warning)" } : undefined}>{value}</div>
      <div className={s.statLabel}>{label}</div>
    </div>
  );
}
